/**
 * HelpPage - Comprehensive documentation and user guide
 * Features all app functionality with screenshots and step-by-step instructions
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  ChevronDown, 
  ChevronRight,
  Rocket,
  FolderOpen,
  MessageCircle,
  CheckSquare,
  HardHat,
  FileText,
  User,
  Users,
  Mic,
  Upload,
  Download,
  Globe,
  Smartphone,
  WifiOff,
  Camera,
  AlertTriangle,
  Home,
  HelpCircle,
  Bell,
  Send,
  Paperclip,
  Table,
  Navigation
} from 'lucide-react';
import { Button } from '../ui/button';

// Help Section Component
const HelpSection = ({ id, icon: Icon, title, children, expanded, onToggle, color = "emerald" }) => {
  const colorClasses = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-700",
    red: "bg-red-50 border-red-200 text-red-700"
  };

  return (
    <div id={id} className="border rounded-lg overflow-hidden mb-4">
      <button
        onClick={onToggle}
        className={`w-full p-4 flex items-center justify-between ${colorClasses[color]} transition-colors`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6" />
          <span className="font-semibold text-lg">{title}</span>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {expanded && (
        <div className="p-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

// Step Component
const Step = ({ number, title, description, tip }) => (
  <div className="flex gap-4 mb-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-semibold text-gray-800 mb-1">{title}</h4>
      <p className="text-gray-600 text-sm">{description}</p>
      {tip && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          💡 <strong>Tipp:</strong> {tip}
        </div>
      )}
    </div>
  </div>
);

// Quick Guide Component
const QuickGuide = ({ steps, t }) => (
  <div className="bg-gray-50 rounded-lg p-4 mb-4">
    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
      <Rocket className="w-4 h-4" />
      {t('help.quickGuide', 'Quick Guide')}
    </h4>
    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
      {steps.map((step, i) => (
        <li key={i}>{step}</li>
      ))}
    </ol>
  </div>
);

// Icon Legend Component
const IconLegend = ({ icon: Icon, name, description, color = "emerald" }) => (
  <div className="flex items-start gap-3 p-3 bg-white border rounded-lg mb-2">
    <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-${color}-100 text-${color}-600 flex items-center justify-center`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-800">{name}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  </div>
);

// Task Guide Component - Step by step for common tasks
const TaskGuide = ({ title, icon: Icon, steps }) => (
  <div className="bg-white border rounded-lg p-4 mb-3">
    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-emerald-600" />}
      {title}
    </h4>
    <ol className="space-y-2">
      {steps.map((step, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center font-bold">
            {i + 1}
          </span>
          <span className="text-gray-700">{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

const HelpPage = ({ onClose, currentUser }) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState(['icons-navigation']);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const expandAll = () => {
    setExpandedSections([
      'icons-navigation', 'getting-started', 'projects', 'communication', 
      'tasks', 'construction', 'documents'
    ]);
  };

  const collapseAll = () => {
    setExpandedSections([]);
  };

  // Table of Contents
  const sections = [
    { id: 'icons-navigation', icon: Navigation, title: t('help.iconsNavigation.title', 'Icons & Navigation'), color: 'emerald' },
    { id: 'getting-started', icon: Rocket, title: t('help.gettingStarted.title', 'Getting Started'), color: 'blue' },
    { id: 'projects', icon: FolderOpen, title: t('help.projects.title', 'Projects'), color: 'purple' },
    { id: 'communication', icon: MessageCircle, title: t('help.communication.title', 'Communication'), color: 'orange' },
    { id: 'tasks', icon: CheckSquare, title: t('help.tasks.title', 'Tasks'), color: 'yellow' },
    { id: 'construction', icon: HardHat, title: t('help.construction.title', 'Field Mode'), color: 'red' },
    { id: 'documents', icon: FileText, title: t('help.documents.title', 'Documents & Reports'), color: 'blue' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-gray-800">
            {t('help.title', 'Help & Documentation')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            {t('help.expandAll', 'Expand all')}
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            {t('help.collapseAll', 'Collapse all')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 sm:p-6">
          
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 mb-6 text-white">
            <h2 className="text-2xl font-bold mb-2">
              {t('help.welcome.title', 'Welcome to Babelmix!')}
            </h2>
            <p className="opacity-90">
              {t('help.welcome.subtitle', 'Your multilingual project communication platform. Find answers to all your questions here.')}
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="bg-white rounded-lg p-4 mb-6 border">
            <h3 className="font-semibold text-gray-700 mb-3">
              {t('help.quickNav', 'Quick Navigation')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => {
                    setExpandedSections(prev => 
                      prev.includes(section.id) ? prev : [...prev, section.id]
                    );
                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 text-left text-sm"
                >
                  <section.icon className="w-4 h-4 text-gray-500" />
                  <span>{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* === SECTION 0: Icons & Navigation === */}
          <HelpSection
            id="icons-navigation"
            icon={Navigation}
            title={t('help.iconsNavigation.title', 'Icons & Navigation')}
            expanded={expandedSections.includes('icons-navigation')}
            onToggle={() => toggleSection('icons-navigation')}
            color="emerald"
          >
            {/* Essential Icons */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.iconsNavigation.essentialIcons', 'Essential Icons at a Glance')}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {t('help.iconsNavigation.essentialIconsDesc', 'These icons are your main navigation tools in Babelmix:')}
              </p>
              
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                    <FolderOpen className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('help.iconsNavigation.icons.folder.name', 'Project Folder')}</h4>
                    <p className="text-sm text-gray-600">{t('help.iconsNavigation.icons.folder.desc', 'Opens your project list – This is your control center! Everything starts here.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                    <Home className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('help.iconsNavigation.icons.home.name', 'Home')}</h4>
                    <p className="text-sm text-gray-600">{t('help.iconsNavigation.icons.home.desc', 'Shows all messages from all your projects in one feed.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('help.iconsNavigation.icons.help.name', 'Help')}</h4>
                    <p className="text-sm text-gray-600">{t('help.iconsNavigation.icons.help.desc', 'Opens this help guide.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                    <Bell className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('help.iconsNavigation.icons.bell.name', 'Notifications')}</h4>
                    <p className="text-sm text-gray-600">{t('help.iconsNavigation.icons.bell.desc', 'Shows your notifications and mentions.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('help.iconsNavigation.icons.avatar.name', 'Avatar / Profile')}</h4>
                    <p className="text-sm text-gray-600">{t('help.iconsNavigation.icons.avatar.desc', 'Your profile settings, language selection, and logout.')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Common Tasks - Step by Step */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.iconsNavigation.commonTasks', 'Common Tasks – Step by Step')}
              </h3>
              
              {/* Task: Write a message */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.writeMessage.title', '"I want to write a message in a project"')}
                icon={MessageCircle}
                steps={[
                  t('help.iconsNavigation.tasks.writeMessage.step1', 'Tap the 📁 Folder icon (top left)'),
                  t('help.iconsNavigation.tasks.writeMessage.step2', 'Select your project from the list'),
                  t('help.iconsNavigation.tasks.writeMessage.step3', 'Type your message in the input field at the bottom'),
                  t('help.iconsNavigation.tasks.writeMessage.step4', 'Tap the ➤ blue arrow to send')
                ]}
              />
              
              {/* Task: See all messages */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.allMessages.title', '"I want to see all messages from all my projects"')}
                icon={Home}
                steps={[
                  t('help.iconsNavigation.tasks.allMessages.step1', 'Tap the 🏠 Home icon'),
                  t('help.iconsNavigation.tasks.allMessages.step2', 'You will see a combined feed of all project messages'),
                  t('help.iconsNavigation.tasks.allMessages.step3', 'Tap any message to jump to that project')
                ]}
              />
              
              {/* Task: Create a project */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.createProject.title', '"I want to create a new project"')}
                icon={FolderOpen}
                steps={[
                  t('help.iconsNavigation.tasks.createProject.step1', 'Tap the 📁 Folder icon'),
                  t('help.iconsNavigation.tasks.createProject.step2', 'Tap "+ New Project"'),
                  t('help.iconsNavigation.tasks.createProject.step3', 'Enter a project name and invite team members')
                ]}
              />
              
              {/* Task: Add a task */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.addTask.title', '"I want to add a task"')}
                icon={CheckSquare}
                steps={[
                  t('help.iconsNavigation.tasks.addTask.step1', 'Open your project (via 📁 Folder)'),
                  t('help.iconsNavigation.tasks.addTask.step2', 'Tap the "Tasks" tab'),
                  t('help.iconsNavigation.tasks.addTask.step3', 'Tap "+ Add Task" and fill in the details')
                ]}
              />
              
              {/* Task: Send voice message - CORRECTED! */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.voiceMessage.title', '"I want to send a voice message"')}
                icon={Mic}
                steps={[
                  t('help.iconsNavigation.tasks.voiceMessage.step1', 'Open your project chat'),
                  t('help.iconsNavigation.tasks.voiceMessage.step2', 'Tap the 🎤 microphone icon once to START recording'),
                  t('help.iconsNavigation.tasks.voiceMessage.step3', 'Speak your message'),
                  t('help.iconsNavigation.tasks.voiceMessage.step4', 'Tap the microphone icon again to STOP and send'),
                  t('help.iconsNavigation.tasks.voiceMessage.step5', 'Your message will be automatically transcribed!')
                ]}
              />
              
              {/* Task: Upload a photo */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.uploadPhoto.title', '"I want to upload a photo"')}
                icon={Camera}
                steps={[
                  t('help.iconsNavigation.tasks.uploadPhoto.step1', 'Open your project chat'),
                  t('help.iconsNavigation.tasks.uploadPhoto.step2', 'Tap the 📎 paperclip or 📷 camera icon'),
                  t('help.iconsNavigation.tasks.uploadPhoto.step3', 'Select or take a photo'),
                  t('help.iconsNavigation.tasks.uploadPhoto.step4', 'Add a comment and send')
                ]}
              />
              
              {/* Task: Track data */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.trackData.title', '"I want to track orders or data"')}
                icon={Table}
                steps={[
                  t('help.iconsNavigation.tasks.trackData.step1', 'Open your project'),
                  t('help.iconsNavigation.tasks.trackData.step2', 'Tap the "Data" tab'),
                  t('help.iconsNavigation.tasks.trackData.step3', 'Create a new table or use a template'),
                  t('help.iconsNavigation.tasks.trackData.step4', 'Add rows and update status as needed')
                ]}
              />
              
              {/* Task: Invite someone */}
              <TaskGuide 
                title={t('help.iconsNavigation.tasks.inviteMember.title', '"I want to invite someone to my project"')}
                icon={Users}
                steps={[
                  t('help.iconsNavigation.tasks.inviteMember.step1', 'Open your project'),
                  t('help.iconsNavigation.tasks.inviteMember.step2', 'Tap the "Info" tab'),
                  t('help.iconsNavigation.tasks.inviteMember.step3', 'Tap "Invite Members"'),
                  t('help.iconsNavigation.tasks.inviteMember.step4', 'Share the invite link or enter their phone number')
                ]}
              />
            </div>

            {/* Pro Tips */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.iconsNavigation.proTips.title', 'Pro Tips')}
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    💡 <strong>{t('help.iconsNavigation.proTips.tip1.title', 'The 📁 Folder icon is your best friend!')}</strong><br/>
                    {t('help.iconsNavigation.proTips.tip1.desc', 'Everything starts here – projects, settings, navigation.')}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>{t('help.iconsNavigation.proTips.tip2.title', "Can't find something?")}</strong><br/>
                    {t('help.iconsNavigation.proTips.tip2.desc', 'Tap 🏠 Home to see all recent activity across projects.')}
                  </p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    💡 <strong>{t('help.iconsNavigation.proTips.tip3.title', 'Working offline?')}</strong><br/>
                    {t('help.iconsNavigation.proTips.tip3.desc', 'Your messages will be saved and sent when you are back online.')}
                  </p>
                </div>
              </div>
            </div>
          </HelpSection>

          {/* === SECTION 1: Getting Started === */}
          <HelpSection
            id="getting-started"
            icon={Rocket}
            title={t('help.gettingStarted.title', 'Getting Started')}
            expanded={expandedSections.includes('getting-started')}
            onToggle={() => toggleSection('getting-started')}
            color="emerald"
          >
            {/* Registration & Login */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-600" />
                {t('help.gettingStarted.registration.title', 'Registration & Login')}
              </h3>
              
              <QuickGuide t={t} steps={[
                t('help.gettingStarted.registration.quick1', 'Fill out the form (name, phone, language)'),
                t('help.gettingStarted.registration.quick2', 'Click "Register"'),
                t('help.gettingStarted.registration.quick3', 'Receive SMS code and enter it'),
                t('help.gettingStarted.registration.quick4', 'Done! You are now logged in')
              ]} />

              {/* Step-by-Step Text Guide (no images) */}
              <div className="space-y-4 mt-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">1. {t('help.gettingStarted.registration.step1.title', 'Open the app')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.gettingStarted.registration.step1.desc', 'Open Babelmix in your browser or as a mobile app.')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">2. {t('help.gettingStarted.registration.step2.title', 'Fill out the form')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.gettingStarted.registration.step2.desc', 'Enter your name, mobile phone number with country code, and select your preferred language.')}</p>
                  <p className="text-blue-600 text-xs mt-1">💡 {t('help.gettingStarted.registration.step2.tip', 'Use a number you have access to - you will receive an SMS verification code.')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">3. {t('help.gettingStarted.registration.step3.title', 'Register & receive code')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.gettingStarted.registration.step3.desc', 'Click "Register". You will receive a 6-digit code via SMS to your phone number.')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">4. {t('help.gettingStarted.registration.step4.title', 'Enter code')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.gettingStarted.registration.step4.desc', 'Enter the received code to complete your registration. After that, you are logged in!')}</p>
                </div>
              </div>
            </div>

            {/* Language Selection */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-600" />
                {t('help.gettingStarted.language.title', 'Language Selection')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('help.gettingStarted.language.desc', 'Babelmix supports 18 languages. You can change your language at any time.')}
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>{t('help.gettingStarted.language.howTo', 'How to change language:')}</strong><br/>
                  {t('help.gettingStarted.language.howToDesc', 'Click on the flag icon in the top left corner of the screen. Select your preferred language from the dropdown menu.')}
                </p>
              </div>
            </div>

            {/* Mobile Installation */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-600" />
                {t('help.gettingStarted.mobile.title', 'Install as Mobile App')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('help.gettingStarted.mobile.desc', 'Babelmix can be installed as a Progressive Web App (PWA) on your smartphone for quick access.')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">iOS (Safari)</h4>
                  <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                    <li>{t('help.gettingStarted.mobile.ios1', 'Open Babelmix in Safari')}</li>
                    <li>{t('help.gettingStarted.mobile.ios2', 'Tap the Share button')}</li>
                    <li>{t('help.gettingStarted.mobile.ios3', 'Select "Add to Home Screen"')}</li>
                  </ol>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Android (Chrome)</h4>
                  <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                    <li>{t('help.gettingStarted.mobile.android1', 'Open Babelmix in Chrome')}</li>
                    <li>{t('help.gettingStarted.mobile.android2', 'Tap the menu (3 dots)')}</li>
                    <li>{t('help.gettingStarted.mobile.android3', 'Select "Install app"')}</li>
                  </ol>
                </div>
              </div>
            </div>
          </HelpSection>

          {/* === SECTION 2: Projects === */}
          <HelpSection
            id="projects"
            icon={FolderOpen}
            title={t('help.projects.title', 'Projects')}
            expanded={expandedSections.includes('projects')}
            onToggle={() => toggleSection('projects')}
            color="blue"
          >
            {/* Create Project */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.projects.create.title', 'Create Your First Project')}
              </h3>
              
              <QuickGuide t={t} steps={[
                t('help.projects.create.quick1', 'Open the sidebar'),
                t('help.projects.create.quick2', 'Click "+ New"'),
                t('help.projects.create.quick3', 'Enter project name'),
                t('help.projects.create.quick4', 'Done! Your project is ready')
              ]} />

              {/* Step-by-Step Text Guide (no images) */}
              <div className="space-y-4 mt-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">1. {t('help.projects.create.step1.title', 'Open the sidebar')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.projects.create.step1.desc', 'Click the folder icon to open the sidebar with your projects.')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">2. {t('help.projects.create.step2.title', 'Click "New"')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.projects.create.step2.desc', 'Click the "+ New" button next to "Projects" to create a new project.')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">3. {t('help.projects.create.step3.title', 'Enter project name')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.projects.create.step3.desc', 'Give your project a clear name that your team will recognize.')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-1">4. {t('help.projects.create.step4.title', 'Project created!')}</h4>
                  <p className="text-gray-600 text-sm">{t('help.projects.create.step4.desc', 'Your project appears in the sidebar. Click to open and start collaborating!')}</p>
                </div>
              </div>
            </div>

            {/* Invite Members */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                {t('help.projects.invite.title', 'Invite Team Members')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.projects.invite.desc', 'Add colleagues, contractors, and partners to your project so everyone can communicate and collaborate.')}
              </p>

              <div className="space-y-4">
                <Step
                  number={1}
                  title={t('help.projects.invite.step1.title', 'Open project')}
                  description={t('help.projects.invite.step1.desc', 'Click on the project you want to add members to. Navigate to the "Members" tab.')}
                />
                <Step
                  number={2}
                  title={t('help.projects.invite.step2.title', 'Add member')}
                  description={t('help.projects.invite.step2.desc', 'Click "Add Member" and enter the phone number of the person you want to invite. You can also select from your existing contacts.')}
                />
                <Step
                  number={3}
                  title={t('help.projects.invite.step3.title', 'Send invitation')}
                  description={t('help.projects.invite.step3.desc', 'The invited person will receive an SMS with an invitation link. Once they register/login, they can access the project.')}
                  tip={t('help.projects.invite.step3.tip', 'People who are already using Babelmix will see the project immediately after being added.')}
                />
              </div>
            </div>
          </HelpSection>

          {/* === SECTION 3: Communication === */}
          <HelpSection
            id="communication"
            icon={MessageCircle}
            title={t('help.communication.title', 'Communication')}
            expanded={expandedSections.includes('communication')}
            onToggle={() => toggleSection('communication')}
            color="purple"
          >
            {/* Chat */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.communication.chat.title', 'Using the Chat')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.communication.chat.desc', 'Each project has its own chat where all members can communicate. Messages are automatically translated to each member\'s preferred language.')}
              </p>

              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                <p className="text-purple-800 text-sm">
                  <strong>✨ {t('help.communication.chat.feature', 'Automatic Translation:')}</strong><br/>
                  {t('help.communication.chat.featureDesc', 'Write in your language - your message will be automatically translated for team members who use other languages.')}
                </p>
              </div>

              <div className="space-y-4">
                <Step
                  number={1}
                  title={t('help.communication.chat.step1.title', 'Open project chat')}
                  description={t('help.communication.chat.step1.desc', 'Click on a project and navigate to the "Chat" tab. You will see all previous messages.')}
                />
                <Step
                  number={2}
                  title={t('help.communication.chat.step2.title', 'Write message')}
                  description={t('help.communication.chat.step2.desc', 'Type your message in the text field at the bottom. Press Enter or click Send to send.')}
                />
              </div>
            </div>

            {/* Photos */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Camera className="w-5 h-5 text-purple-600" />
                {t('help.communication.photos.title', 'Photos & Documentation')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.communication.photos.desc', 'Capture and share photos directly in the chat. Perfect for documenting progress, reporting issues, or sharing important information visually.')}
              </p>

              <QuickGuide t={t} steps={[
                t('help.communication.photos.quick1', 'Click the camera/image icon'),
                t('help.communication.photos.quick2', 'Take a photo or select from gallery'),
                t('help.communication.photos.quick3', 'Add an optional description'),
                t('help.communication.photos.quick4', 'Send - photo is visible to all team members')
              ]} />

              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>📸 {t('help.communication.photos.useCase', 'Use cases:')}</strong><br/>
                  {t('help.communication.photos.useCaseDesc', '• Document work progress • Report defects or issues • Share delivery receipts • Capture safety concerns • Show material conditions')}
                </p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 text-sm">
                  <strong>💡 {t('help.communication.photos.tip', 'Pro Tip:')}</strong><br/>
                  {t('help.communication.photos.tipDesc', 'Add a short description to each photo so team members know exactly what they are looking at - especially useful when documenting issues.')}
                </p>
              </div>
            </div>

            {/* Voice Messages */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Mic className="w-5 h-5 text-purple-600" />
                {t('help.communication.voice.title', 'Voice Messages')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.communication.voice.desc', 'On the construction site and don\'t have time to type? Send a voice message! It will be automatically transcribed to text.')}
              </p>

              <QuickGuide t={t} steps={[
                t('help.communication.voice.quick1', 'Click microphone button (recording starts)'),
                t('help.communication.voice.quick2', 'Speak your message'),
                t('help.communication.voice.quick3', 'Click again (recording stops)'),
                t('help.communication.voice.quick4', 'Message is transcribed and translated')
              ]} />

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 text-sm">
                  <strong>💡 {t('help.communication.voice.tip', 'Pro Tip:')}</strong><br/>
                  {t('help.communication.voice.tipDesc', 'Speak clearly and in a quiet environment for best transcription results.')}
                </p>
              </div>
            </div>

            {/* File Sharing */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-600" />
                {t('help.communication.files.title', 'Sharing Files')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.communication.files.desc', 'Share documents, photos, and plans directly in the chat or in the project\'s file area.')}
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">{t('help.communication.files.supported', 'Supported formats')}</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>📄 PDF, Word, Excel</li>
                    <li>🖼️ JPG, PNG, HEIC</li>
                    <li>📐 DXF, DWG (CAD)</li>
                    <li>🎥 MP4, MOV (Video)</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">{t('help.communication.files.howTo', 'How to upload')}</h4>
                  <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                    <li>{t('help.communication.files.how1', 'Click the paperclip icon')}</li>
                    <li>{t('help.communication.files.how2', 'Select file(s)')}</li>
                    <li>{t('help.communication.files.how3', 'Add optional caption')}</li>
                    <li>{t('help.communication.files.how4', 'Click Send')}</li>
                  </ol>
                </div>
              </div>
            </div>
          </HelpSection>

          {/* === SECTION 4: Tasks === */}
          <HelpSection
            id="tasks"
            icon={CheckSquare}
            title={t('help.tasks.title', 'Tasks')}
            expanded={expandedSections.includes('tasks')}
            onToggle={() => toggleSection('tasks')}
            color="orange"
          >
            {/* Create Task */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.tasks.create.title', 'Creating Tasks')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.tasks.create.desc', 'Organize work by creating tasks. Assign them to team members, set priorities, and track progress.')}
              </p>

              <QuickGuide t={t} steps={[
                t('help.tasks.create.quick1', 'Go to Tasks tab'),
                t('help.tasks.create.quick2', 'Click "New Task"'),
                t('help.tasks.create.quick3', 'Enter title and details'),
                t('help.tasks.create.quick4', 'Assign and set due date'),
                t('help.tasks.create.quick5', 'Save task')
              ]} />

              <div className="space-y-4">
                <Step
                  number={1}
                  title={t('help.tasks.create.step1.title', 'Navigate to Tasks')}
                  description={t('help.tasks.create.step1.desc', 'Open your project and click on the "Tasks" tab to see all existing tasks.')}
                />
                <Step
                  number={2}
                  title={t('help.tasks.create.step2.title', 'Create new task')}
                  description={t('help.tasks.create.step2.desc', 'Click "New Task" and fill in the title. Add a description with details about what needs to be done.')}
                  tip={t('help.tasks.create.step2.tip', 'Use voice input to quickly describe the task - just click the microphone icon!')}
                />
                <Step
                  number={3}
                  title={t('help.tasks.create.step3.title', 'Set priority and assignee')}
                  description={t('help.tasks.create.step3.desc', 'Choose priority (normal or urgent). Assign to a specific person or leave open for anyone to claim.')}
                />
                <Step
                  number={4}
                  title={t('help.tasks.create.step4.title', 'Add due date and tags')}
                  description={t('help.tasks.create.step4.desc', 'Set a deadline and add category tags like "Electric", "Plumbing" to organize tasks.')}
                />
              </div>
            </div>

            {/* Complete Task */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.tasks.complete.title', 'Completing Tasks')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.tasks.complete.desc', 'When you finish a task, mark it complete with optional proof photos and notes.')}
              </p>

              <div className="space-y-4">
                <Step
                  number={1}
                  title={t('help.tasks.complete.step1.title', 'Open task details')}
                  description={t('help.tasks.complete.step1.desc', 'Click on the task you want to complete to open its details.')}
                />
                <Step
                  number={2}
                  title={t('help.tasks.complete.step2.title', 'Add completion proof')}
                  description={t('help.tasks.complete.step2.desc', 'Take photos of the completed work. Add notes about what was done or any issues encountered.')}
                />
                <Step
                  number={3}
                  title={t('help.tasks.complete.step3.title', 'Submit for review')}
                  description={t('help.tasks.complete.step3.desc', 'Click "Submit for review". The project creator can then approve or request changes.')}
                />
              </div>
            </div>

            {/* Quick Documentation */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Camera className="w-5 h-5 text-orange-600" />
                {t('help.tasks.quickDoc.title', 'Quick Documentation')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.tasks.quickDoc.desc', 'Quickly document progress on the construction site with photos and voice notes - no need to create a formal task.')}
              </p>

              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">{t('help.tasks.quickDoc.howTo', 'How it works:')}</h4>
                <ol className="list-decimal list-inside text-sm text-orange-700 space-y-1">
                  <li>{t('help.tasks.quickDoc.how1', 'Click the camera/document button')}</li>
                  <li>{t('help.tasks.quickDoc.how2', 'Take photos of your work')}</li>
                  <li>{t('help.tasks.quickDoc.how3', 'Add voice or text description')}</li>
                  <li>{t('help.tasks.quickDoc.how4', 'Select category')}</li>
                  <li>{t('help.tasks.quickDoc.how5', 'Save - documentation is created automatically')}</li>
                </ol>
              </div>
            </div>
          </HelpSection>

          {/* === SECTION 5: Construction Mode === */}
          <HelpSection
            id="construction"
            icon={HardHat}
            title={t('help.construction.title', 'Construction Mode')}
            expanded={expandedSections.includes('construction')}
            onToggle={() => toggleSection('construction')}
            color="yellow"
          >
            {/* What is Construction Mode */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.construction.what.title', 'What is Construction Mode?')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.construction.what.desc', 'Construction Mode is designed for on-site use. Large buttons, optimized for gloves, and full offline support for areas with poor connectivity.')}
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-yellow-50 p-4 rounded-lg text-center">
                  <Camera className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-yellow-800">{t('help.construction.feature1.title', 'Photo Documentation')}</h4>
                  <p className="text-sm text-yellow-700">{t('help.construction.feature1.desc', 'Quick photos with automatic timestamps')}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg text-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-yellow-800">{t('help.construction.feature2.title', 'Defect Reporting')}</h4>
                  <p className="text-sm text-yellow-700">{t('help.construction.feature2.desc', 'Report issues with photos and voice')}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg text-center">
                  <WifiOff className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-yellow-800">{t('help.construction.feature3.title', 'Offline Mode')}</h4>
                  <p className="text-sm text-yellow-700">{t('help.construction.feature3.desc', 'Works without internet connection')}</p>
                </div>
              </div>
            </div>

            {/* Report Defect */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                {t('help.construction.defect.title', 'Reporting Defects')}
              </h3>
              
              <QuickGuide t={t} steps={[
                t('help.construction.defect.quick1', 'Open Construction Mode'),
                t('help.construction.defect.quick2', 'Tap "Report Defect"'),
                t('help.construction.defect.quick3', 'Take photo of the defect'),
                t('help.construction.defect.quick4', 'Describe via voice or text'),
                t('help.construction.defect.quick5', 'Submit - task is created automatically')
              ]} />

              <div className="space-y-4">
                <Step
                  number={1}
                  title={t('help.construction.defect.step1.title', 'Enter Construction Mode')}
                  description={t('help.construction.defect.step1.desc', 'From the main screen, tap on "Construction Mode". The interface changes to large, easy-to-tap buttons.')}
                />
                <Step
                  number={2}
                  title={t('help.construction.defect.step2.title', 'Report defect')}
                  description={t('help.construction.defect.step2.desc', 'Tap the "Report Defect" button. Your camera will open to take a photo.')}
                />
                <Step
                  number={3}
                  title={t('help.construction.defect.step3.title', 'Document the issue')}
                  description={t('help.construction.defect.step3.desc', 'Take clear photos of the defect. Add a voice description - this is faster than typing on site.')}
                  tip={t('help.construction.defect.step3.tip', 'Take multiple photos from different angles for better documentation.')}
                />
                <Step
                  number={4}
                  title={t('help.construction.defect.step4.title', 'Set priority and submit')}
                  description={t('help.construction.defect.step4.desc', 'Mark urgent defects appropriately. The report is automatically converted into a task.')}
                />
              </div>
            </div>

            {/* Offline Mode */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <WifiOff className="w-5 h-5 text-yellow-600" />
                {t('help.construction.offline.title', 'Offline Mode')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.construction.offline.desc', 'No internet on site? No problem! Babelmix saves your work locally and syncs when you\'re back online.')}
              </p>

              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-2">{t('help.construction.offline.howWorks', 'How offline mode works:')}</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>✓ {t('help.construction.offline.work1', 'Photos and reports are saved locally')}</li>
                  <li>✓ {t('help.construction.offline.work2', 'An indicator shows pending uploads')}</li>
                  <li>✓ {t('help.construction.offline.work3', 'Automatic sync when connection returns')}</li>
                  <li>✓ {t('help.construction.offline.work4', 'Manual sync option available')}</li>
                </ul>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>⚠️ {t('help.construction.offline.note', 'Note:')}</strong><br/>
                  {t('help.construction.offline.noteDesc', 'You can view existing project data offline, but new messages from others won\'t appear until you\'re online again.')}
                </p>
              </div>
            </div>
          </HelpSection>

          {/* === SECTION 6: Documents & Reports === */}
          <HelpSection
            id="documents"
            icon={FileText}
            title={t('help.documents.title', 'Documents & Reports')}
            expanded={expandedSections.includes('documents')}
            onToggle={() => toggleSection('documents')}
            color="red"
          >
            {/* File Management */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.documents.files.title', 'File Management')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.documents.files.desc', 'All project files are stored in one place. Upload plans, photos, documents - everything your team needs.')}
              </p>

              <div className="space-y-4">
                <Step
                  number={1}
                  title={t('help.documents.files.step1.title', 'Go to Files tab')}
                  description={t('help.documents.files.step1.desc', 'Open your project and click on the "Files" tab to see all uploaded files.')}
                />
                <Step
                  number={2}
                  title={t('help.documents.files.step2.title', 'Upload files')}
                  description={t('help.documents.files.step2.desc', 'Click "Upload" and select files from your device. You can upload multiple files at once.')}
                />
                <Step
                  number={3}
                  title={t('help.documents.files.step3.title', 'Preview and download')}
                  description={t('help.documents.files.step3.desc', 'Click on any file to preview it. PDFs, images, and even CAD files can be viewed directly in the browser.')}
                />
              </div>
            </div>

            {/* CAD Preview */}
            <div className="mb-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('help.documents.cad.title', 'CAD/DXF Preview')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.documents.cad.desc', 'View CAD files (DXF format) directly in Babelmix without special software.')}
              </p>

              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-800 mb-2">{t('help.documents.cad.features', 'CAD preview features:')}</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>🔍 {t('help.documents.cad.feature1', 'Zoom and pan to navigate')}</li>
                  <li>📏 {t('help.documents.cad.feature2', 'View layers and dimensions')}</li>
                  <li>🎨 {t('help.documents.cad.feature3', 'Color-coded elements')}</li>
                  <li>📱 {t('help.documents.cad.feature4', 'Works on mobile devices')}</li>
                </ul>
              </div>
            </div>

            {/* Generate Reports */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Download className="w-5 h-5 text-red-600" />
                {t('help.documents.reports.title', 'Generate Reports')}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {t('help.documents.reports.desc', 'Create professional DOCX reports with all project documentation for a selected time period.')}
              </p>

              <QuickGuide t={t} steps={[
                t('help.documents.reports.quick1', 'Go to project Tasks tab'),
                t('help.documents.reports.quick2', 'Find "Create Documentation" section'),
                t('help.documents.reports.quick3', 'Select date range'),
                t('help.documents.reports.quick4', 'Click "Download DOCX Report"'),
                t('help.documents.reports.quick5', 'Report downloads automatically')
              ]} />

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">{t('help.documents.reports.includes', 'Report includes:')}</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>✓ {t('help.documents.reports.inc1', 'All tasks with status and assignees')}</li>
                  <li>✓ {t('help.documents.reports.inc2', 'Photos and documentation')}</li>
                  <li>✓ {t('help.documents.reports.inc3', 'Completion proofs')}</li>
                  <li>✓ {t('help.documents.reports.inc4', 'Timeline of activities')}</li>
                </ul>
              </div>
            </div>
          </HelpSection>

          {/* Footer */}
          <div className="mt-8 p-6 bg-white rounded-lg border text-center">
            <h3 className="font-semibold text-gray-800 mb-2">
              {t('help.footer.title', 'Still have questions?')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('help.footer.desc', 'Contact us if you need additional help or have suggestions for improvement.')}
            </p>
            <p className="text-sm text-gray-500">
              📧 support@babelmix.com
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HelpPage;
